import { useContext, useEffect, useState } from 'react';
import { Button, Spinner, Table } from 'react-bootstrap';
import { withTranslation } from 'react-i18next';
import { BiCheck, BiSolidCommentDetail, BiX } from 'react-icons/bi';
import { BsCheck } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import { LoaderContext } from '../../../App';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import MessageModal from '../../../components/MessageModal/MessageModal';
import CompanyService from '../../../core/service/CompanyService';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import './GHGEmissions.scss';

const ReviewAndFinalize = ({ t, filterData, prevPage, noShowFooter }) => {
    const data = JSON.parse(localStorage.getItem("authority"));
    const companyId = data.profile.company.id;
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [orgData, setOrgData] = useState([]);
    const [oprData, setOprData] = useState([]);
    const [emissionSourceData, setEmissionSourceData] = useState([]);
    const [finalized, setfinalized] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [matchedHierarchy, setMatchedHierarchy] = useState([]);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const navigate = useNavigate();
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [scope1Display, setScope1Display] = useState(true);
    const [scope2Display, setScope2Display] = useState(true);
    const [scope3Display, setScope3Display] = useState(true);
    const [scopesWithYesCategories, setScopesWithYesCategories] = useState([]);
    const [hasAnswered, setHasAnswered] = useState(false);
    const [hasSelSources, setHasSelSources] = useState(false);
    const [hasAnySources, setHasAnySources] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [enterpriseLoaded, setEnterpriseLoaded] = useState(false);
    const [summaryLoaded, setSummaryLoaded] = useState(false);
    const [orgBoundaryLoading, setOrgBoundaryLoading] = useState(true);
    const [oprBoundaryLoading, setOprBoundaryLoading] = useState(true);
    const [emissionSourceLoading, setEmissionSourceLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        getSummary();
    }, [filterData])

    useEffect(() => {
        setIsLoading(true);
        getEnterpriseStructure();
    }, [])

    useEffect(() => {
        if (nodes.length > 0 && orgData.length > 0) {
            searchMatching(orgData);
        }
    }, [nodes, orgData])

    useEffect(() => {
        if (enterpriseLoaded && summaryLoaded) {
            setIsLoading(false);
        }
    }, [enterpriseLoaded, summaryLoaded])

    const searchMatching = (orgData) => {
        const businessUnitIds = new Set(orgData.map(item => item.businessUnitId));
        const hierarchy = buildHierarchy(nodes, businessUnitIds);
        setMatchedHierarchy(hierarchy);
    }

    const buildHierarchy = (units, targetIds) => {
        const result = [];

        units.forEach(unit => {
            const isTarget = targetIds.has(unit.id);
            let children = [];

            if (unit.children && unit.children.length > 0) {
                children = buildHierarchy(unit.children, targetIds);
            }

            if (isTarget || children.length > 0) {
                const node = {
                    id: unit.id,
                    name: unit.name,
                    unitType: unit.unitType,
                    children: children
                };
                result.push(node);
            }
        });

        return result;
    };

    const renderHierarchy = (nodes, level = 0) => {
        return (
            <div className='GHGInventory_qusmain'>
                <ul>
                    {nodes.length > 0 ? (
                        nodes.map(node => (
                            <li key={node.id}>
                                <div style={{ fontWeight: level === 0 ? 'bold' : 'normal' }}>
                                    {node.name}
                                </div>
                                {node.children && node.children.length > 0 && renderHierarchy(node.children, level + 1)}
                            </li>
                        ))
                    ) : renderEmptyDataLabelOrg()
                    }
                </ul>
            </div>

        );
    };

    const renderEmptyDataLabelOrg = () => {
        return (
            <div className="row m-0">
                <div className='col-lg-12 px-0'>
                    <span><BiSolidCommentDetail className="me-2" />{t('NoOrganizationalBoundariesDefined.label')}</span>
                </div>
            </div>
        )
    };

    const renderEmptyDataLabelOpr = () => {
        return (
            <div className="row m-0">
                <div className='col-lg-12 px-0'>
                    <span><BiSolidCommentDetail className="me-2" />{t('NoOperationalBoundariesDefined.label')}</span>
                </div>
            </div>
        )
    };

    const renderEmptyDataLabelEmission = () => {
        return (
            <div className="row m-0">
                <div className='col-lg-12 p-0'>
                    <span><BiSolidCommentDetail className="me-2" />No emission sources identified</span>
                </div>
            </div>
        )
    };

    const getEnterpriseStructure = () => {
        // setLoaderDisplay(true);
        setOrgBoundaryLoading(true);
        CompanyService.getEnterpriseStructure()
            .then(res => {
                setLoaderDisplay(false);
                if (res.status !== 200) {
                    setEnterpriseLoaded(true);
                    setOrgBoundaryLoading(false);
                    return;
                }
                setNodes(res.data.data ?? []);
                setEnterpriseLoaded(true);
                setOrgBoundaryLoading(false);
            })
            .catch(err => {
                setLoaderDisplay(false);
                setEnterpriseLoaded(true);
                setOrgBoundaryLoading(false);
            });
    };

    const flattenEmissionSources = (emissionSourceData) => {
        // Structure: { "GHG": { "Scope 1": { "Category": [...] } }, "DEFRA": {...}, "CEA": {...} }
        // Need to flatten to: { "Scope 1": { "Category": [...] }, "Scope 2": {...} }
        const flattened = {};

        if (!emissionSourceData || typeof emissionSourceData !== 'object' || Array.isArray(emissionSourceData)) {
            return flattened;
        }

        Object.entries(emissionSourceData).forEach(([dataset, scopes]) => {
            if (!scopes || typeof scopes !== 'object' || Array.isArray(scopes)) {
                return;
            }
            
            Object.entries(scopes).forEach(([scopeName, categories]) => {
                if (!categories || typeof categories !== 'object' || Array.isArray(categories)) {
                    return;
                }
                
                if (!flattened[scopeName]) {
                    flattened[scopeName] = {};
                }
                
                Object.entries(categories).forEach(([categoryName, sources]) => {
                    if (!Array.isArray(sources)) {
                        return;
                    }
                    
                    if (!flattened[scopeName][categoryName]) {
                        flattened[scopeName][categoryName] = [];
                    }
                    // Merge sources from different datasets
                    flattened[scopeName][categoryName] = [
                        ...flattened[scopeName][categoryName],
                        ...sources
                    ];
                });
            });
        });

        return flattened;
    };

    const getSummary = () => {
        // setLoaderDisplay(true);
        setOprBoundaryLoading(true);
        setEmissionSourceLoading(true);
        const date = filterData?.year + '-01-01';
        GHGEmissionService.getSummaryAll(companyId, date)
            .then((response) => {
                setLoaderDisplay(false);
                setOrgData(response.data.data['organizationalBoundary'] || []);
                setOprData(response.data.data['operationalBoundary'] || []);
                setScopesWithYesCategories(processScopes(response.data.data['operationalBoundary'] || []));
                
                // Handle new structure: { "GHG": { "Scope 1": {...} }, "DEFRA": {...}, "CEA": {...} }
                // The API returns emissionSourceAll with dataset keys (GHG, DEFRA, CEA)
                const emissionSourceRaw = response.data.data['emissionSourceAll'] || {};
                const emissionSource = flattenEmissionSources(emissionSourceRaw);
                
                const sortedScopes = Object.entries(emissionSource).sort(([scopeA], [scopeB]) => {
                    const order = { "Scope 1": 1, "Scope 2": 2, "Scope 3": 3 };
                    return (order[scopeA] || 999) - (order[scopeB] || 999);
                });
                setEmissionSourceData(sortedScopes);
                setfinalized(response.data.data['finalized'] || false);
                setHasAnswered(hasAnsweredQuestions(response.data.data['operationalBoundary'] || []));
                
                // Check if there are any selected sources in the flattened structure
                // This is used for the Finalize button - should only be true if sources are selected
                const hasSelected = hasSelectedSources(emissionSource);
                setHasSelSources(hasSelected);
                
                // Also check if there are any sources at all (to show the section)
                // This is used for display purposes - show section if any sources exist
                const hasAnySourcesValue = Object.keys(emissionSource).length > 0 && 
                    Object.values(emissionSource).some(scope => 
                        scope && typeof scope === 'object' && !Array.isArray(scope) &&
                        Object.values(scope).some(category => 
                            Array.isArray(category) && category.length > 0
                        )
                    );
                setHasAnySources(hasAnySourcesValue);
                setSummaryLoaded(true);
                setOprBoundaryLoading(false);
                setEmissionSourceLoading(false);
            })
            .catch((error) => {
                setLoaderDisplay(false);
                setSummaryLoaded(true);
                setOprBoundaryLoading(false);
                setEmissionSourceLoading(false);
            });
    }

    const hasAnsweredQuestions = (data) => {
        for (const scope of data) {
            for (const category of scope.categories) {
                for (const question of category.questions) {
                    if (question.answer === "Yes" || question.answer === "No") {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    const hasSelectedSources = (data) => {
        if (!data || Object.keys(data).length === 0) {
            return false;
        }
        return Object.values(data).some(scope => {
            if (!scope || typeof scope !== 'object' || Array.isArray(scope)) {
                return false;
            }
            return Object.values(scope).some(category => {
                if (!Array.isArray(category)) {
                    return false;
                }
                return category.some(source =>
                    source && (
                    source.alreadySelected === true ||
                        (source.factors && Array.isArray(source.factors) && source.factors.some(factor => factor.alreadySelected === true))
                )
                );
            });
        });
    };

    const processScopes = (data) => {
        return data.map(scope => {
            const yesCategories = scope.categories.filter(category =>
                category.questions.some(question => question.answer === "Yes")
            );
            const noCategories = scope.categories.filter(category =>
                category.questions.some(question => question.answer === "No")
            );
            const yesCategoryNames = yesCategories.map(cat => cat.name);
            const noCategoryNames = noCategories.map(cat => cat.name);
            return {
                scopeName: scope.name,
                categoriesYes: yesCategoryNames,
                categoriesNo: noCategoryNames
            };
        }).filter(scope => scope.categoriesYes.length > 0);
    };

    const finalizeSummary = () => {
        // setLoaderDisplay(true);
        const date = filterData?.year + '-01-01';
        GHGEmissionService.finalizeGHGInventory(companyId, date)
            .then((response) => {
                setLoaderDisplay(false);
                if (response.status !== 200) return;
                setShowMessageModal(true);
            })
            .catch((error) => {
                setLoaderDisplay(false);
            });
    }

    const gotoInventory = () => {
        navigate(`/company_admin/ghg_inventory?year=` + filterData.year + `&table=` + filterData.emissionTable);
    }

    const confirmCancel = (val) => {
        setShowConfirmation(false);
        if (val) {
            finalizeSummary();
        }
    };

    const callbackMessageModal = (boolVal) => {
        setShowMessageModal(false);
        gotoInventory();
    };

    return (
        <>
            <div className='pt-2'>
                <div className='ReviewAndFinalizeCard'>
                    <div className={`ReviewAndFinalizeCardDtRow ${orgData.length > 0 ? 'done' : ''}`}>
                        <div className='ReviewAndFinalizeCardDtRow02 row m-0'>
                            <span class="InventorystepStatus number">1</span>
                            <span className='InventorystepStatus checkIcon'><BsCheck /></span>
                            <div className='col-md-3 ReviewAndFinalizeCardDt p-2 '>
                                <div className='mb-1 ReviewAndFinalizeCardDtTitle'>
                                    {t('InventoryOverview.label')}
                                </div>
                            </div>
                            <div className='col-md-9 p-2'>
                                <ul className='RFCard'>
                                    <li><b>{t('ReportingYear.label')} :</b> {filterData?.year}</li>
                                    <li><b>{t('FinalizedOn.label')} :</b> {new Date().toLocaleDateString()}</li>
                                    <li><b>{t('SubmittedBy.label')} :</b> {filterData.submittedBy}</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className={`ReviewAndFinalizeCardDtRow ${orgData.length > 0 ? 'done' : ''}`}>
                        <div className='ReviewAndFinalizeCardDtRow02 row m-0'>
                            <span class="InventorystepStatus number">2</span>
                            <span className='InventorystepStatus checkIcon'><BsCheck /></span>
                            <div className='col-md-3 ReviewAndFinalizeCardDt p-2 '>
                                <div className='mb-1 ReviewAndFinalizeCardDtTitle'>
                                    {t('OrganizationalBoundaries.label')}
                                </div>
                            </div>
                            <div className='col-md-9 p-2'>
                                {orgBoundaryLoading ? (
                                    <div className="d-flex justify-content-center align-items-center p-4">
                                        <div className="text-center">
                                            <Spinner animation="border" size="sm" role="status" className="mb-2">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <p className="text-muted small mb-0">{t('LoadingOrgBoundaries.label')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    renderHierarchy(matchedHierarchy)
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={`ReviewAndFinalizeCardDtRow ${oprData.length > 0 ? 'done' : ''}`}>
                        <div className='ReviewAndFinalizeCardDtRow02 row m-0'>
                            <span class="InventorystepStatus number">3</span>
                            <span className='InventorystepStatus checkIcon'><BsCheck /></span>
                            <div className='col-md-3 ReviewAndFinalizeCardDt p-2 '>
                                <div className='mb-1 ReviewAndFinalizeCardDtTitle'>
                                    {t('OperationalBoundaries.label')}
                                </div>
                            </div>
                            <div className='col-md-9 p-2'>
                                {oprBoundaryLoading ? (
                                    <div className="d-flex justify-content-center align-items-center p-4">
                                        <div className="text-center">
                                            <Spinner animation="border" size="sm" role="status" className="mb-2">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <p className="text-muted small mb-0">{t('LoadingOprBoundaries.label')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {
                                            hasAnswered &&
                                            <div className='OprBoundaryBody02_inner'>
                                                {/* <p className='text-muted mb-2'>Selected Scope 1, Scope 2, Scope 3 emissions</p> */}
                                                <div className='row m-0'>
                                                    <div className='col-md-12 px-0'>
                                                        <div className='card'>
                                                            <Table hover className='mb-0 table-border-bottom-0'>
                                                                <thead>
                                                                    <tr>
                                                                        <th>{t('EmissionSource.label')}</th>
                                                                        <th>{t('Selected.label')}</th>
                                                                        <th>{t('Omitted.label')}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {scopesWithYesCategories.map((item, index) => (
                                                                        <tr>
                                                                            <td>
                                                                                {item?.scopeName}
                                                                            </td>
                                                                            <td>
                                                                                {/* {item.categories.join(', ')} */}
                                                                                {item.categoriesYes.map(data => (
                                                                                    <div><BiCheck style={{ fontSize: '18px', color: 'green' }} /> {data}</div>
                                                                                ))
                                                                                }
                                                                            </td>
                                                                            <td>
                                                                                {/* {item.categories.join(', ')} */}
                                                                                {item.categoriesNo.map(data => (
                                                                                    <div><BiX style={{ fontSize: '18px', color: 'red' }} /> {data}</div>
                                                                                ))
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                    }
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                        {
                                            !hasAnswered &&
                                            <div className='OprBoundaryBody02_inner'>
                                                {renderEmptyDataLabelOpr()}
                                            </div>
                                        }
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={`ReviewAndFinalizeCardDtRow ${hasSelSources ? 'done' : ''}`}>
                        <div className='ReviewAndFinalizeCardDtRow02 row m-0'>
                            <span class="InventorystepStatus number">4</span>
                            <span className='InventorystepStatus checkIcon'><BsCheck /></span>
                            <div className='col-md-3 ReviewAndFinalizeCardDt p-2 '>
                                <div className='mb-1 ReviewAndFinalizeCardDtTitle'>
                                    {t('EmissionSources.label')}
                                </div>
                            </div>
                            <div className='col-md-9 p-2'>
                                {emissionSourceLoading ? (
                                    <div className="d-flex justify-content-center align-items-center p-4">
                                        <div className="text-center">
                                            <Spinner animation="border" size="sm" role="status" className="mb-2">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <p className="text-muted small mb-0">{t('LoadingEmissionSources.label')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {hasAnySources ? (
                                            emissionSourceData.map(([scopeName, scopeData]) => {
                                                // Check if this scope has any selected items
                                                const hasSelectedItems = Object.values(scopeData).some(categoryItems =>
                                                    categoryItems.some(item => item.alreadySelected)
                                                );
                                                return (
                                                    <div key={scopeName} className='OprBoundaryBody02_inner'>
                                                        <h6 className='fw-700'>{scopeName}</h6>

                                                        {!hasSelectedItems ? (
                                                            <p className='text-muted mb-2'>No items selected in this scope</p>
                                                        ) : (
                                                            Object.entries(scopeData).map(([categoryName, categoryItems]) => {
                                                                // Filter for categories with selected items
                                                                const selectedCategoryItems = categoryItems.filter(item => item.alreadySelected);

                                                                return selectedCategoryItems.length > 0 && (
                                                                    <div key={categoryName} className='row mx-0 mb-2'>
                                                                        <div className=' col-md-12 px-0 ps-3'>
                                                                            <h6>{categoryName}</h6>
                                                                            <div className='card'>
                                                                                <Table hover className='mb-0 table-border-bottom-0'>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th width="35%">Emission Source</th>
                                                                                            <th width="15%" className='text-center'>CO2 factor</th>
                                                                                            <th width="20%" className='text-center'>Unit</th>
                                                                                            <th width="30%">Notes</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {selectedCategoryItems.map(source => (
                                                                                            <tr key={source.sourceId}>
                                                                                                <td>{source.name}</td>
                                                                                                <td className='text-center'>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2'))
                                                                                                        .map(factor => (factor.efValue))
                                                                                                    }
                                                                                                </td>
                                                                                                <td className='text-center'>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2'))
                                                                                                        .map(factor => (factor.unit))
                                                                                                    }
                                                                                                </td>
                                                                                                <td>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2'))
                                                                                                        .map(factor => (factor.notes))
                                                                                                    }
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </Table>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                );

                                            })
                                        ) :
                                            <div className='OprBoundaryBody02_inner'>
                                                {renderEmptyDataLabelEmission()}
                                            </div>
                                        }
                                    </>
                                )}
                            </div>
                        </div>
                    </div>



                    {/* <div key="1" className="card mb-2 ReviewAndFinalizeCardIn">
                        <div className="GHGInventory_qusBox">
                            <span onClick={(e) => {
                                setScope1Display(!scope1Display);
                            }}>
                                {!scope1Display && <BiChevronDown />}
                                {scope1Display && <BiChevronUp />}
                            </span>
                            <h6 className="mb-0" key="1">1. Organizational Boundaries</h6>
                        </div>
                        <Collapse in={scope1Display === true}>
                            {orgBoundaryLoading ? (
                                <div className="d-flex justify-content-center align-items-center p-4">
                                    <div className="text-center">
                                        <Spinner animation="border" size="sm" role="status" className="mb-2">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                        <p className="text-muted small mb-0">{t('LoadingOrgBoundaries.label')}</p>
                                    </div>
                                </div>
                            ) : (
                                renderHierarchy(matchedHierarchy)
                            )}
                        </Collapse>
                    </div>
                    <div key="2" className="card mb-2 ReviewAndFinalizeCardIn">
                        <div className="GHGInventory_qusBox">
                            <span onClick={(e) => {
                                setScope2Display(!scope2Display);
                            }}>
                                {!scope2Display && <BiChevronDown />}
                                {scope2Display && <BiChevronUp />}
                            </span>
                            <h6 className="mb-0" key="1">2. Operational Boundaries</h6>
                        </div>
                        <Collapse in={scope2Display === true}>
                            <div className="card-body p-0 OprBoundaryBody02">
                                {oprBoundaryLoading ? (
                                    <div className="d-flex justify-content-center align-items-center p-4">
                                        <div className="text-center">
                                            <Spinner animation="border" size="sm" role="status" className="mb-2">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <p className="text-muted small mb-0">{t('LoadingOprBoundaries.label')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {
                                            hasAnswered &&
                                            <div className='OprBoundaryBody02_inner'>
                                                <div className='row m-0'>
                                                    <div className='col-md-10 px-0'>
                                                        <div className='card'>
                                                            <Table hover className='mb-0 table-border-bottom-0'>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Emissions</th>
                                                                        <th>Selected</th>
                                                                        <th>Omitted</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {scopesWithYesCategories.map((item, index) => (
                                                                        <tr>
                                                                            <td>
                                                                                {item?.scopeName}
                                                                            </td>
                                                                            <td>
                                                                                {item.categoriesYes.map(data => (
                                                                                    <div><BiCheck style={{ fontSize: '18px', color: 'green' }} /> {data}</div>
                                                                                ))
                                                                                }
                                                                            </td>
                                                                            <td>
                                                                                {item.categoriesNo.map(data => (
                                                                                    <div><BiX style={{ fontSize: '18px', color: 'red' }} /> {data}</div>
                                                                                ))
                                                                                }
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                    }
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        }
                                        {
                                            !hasAnswered &&
                                            <div className='OprBoundaryBody02_inner'>
                                                {renderEmptyDataLabelOpr()}
                                            </div>
                                        }
                                    </>
                                )}
                            </div>
                        </Collapse>
                    </div>
                    <div key="3" className="card mb-2 ReviewAndFinalizeCardIn">
                        <div className="GHGInventory_qusBox">
                            <span onClick={(e) => {
                                setScope3Display(!scope3Display);
                            }}>
                                {!scope3Display && <BiChevronDown />}
                                {scope3Display && <BiChevronUp />}
                            </span>
                            <h6 className="mb-0" key="1">3. Emission Sources</h6>
                        </div>
                        <Collapse in={scope3Display === true}>
                            <div className="card-body p-0 OprBoundaryBody02 mb-3">
                                {emissionSourceLoading ? (
                                    <div className="d-flex justify-content-center align-items-center p-4">
                                        <div className="text-center">
                                            <Spinner animation="border" size="sm" role="status" className="mb-2">
                                                <span className="visually-hidden">Loading...</span>
                                            </Spinner>
                                            <p className="text-muted small mb-0">{t('LoadingEmissionSources.label')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {hasSelSources ? (
                                            emissionSourceData.map(([scopeName, scopeData]) => {
                                                // Check if this scope has any selected items
                                                const hasSelectedItems = Object.values(scopeData).some(categoryItems =>
                                                    categoryItems.some(item => item.alreadySelected)
                                                );
                                                return (
                                                    <div key={scopeName} className='OprBoundaryBody02_inner'>
                                                        <h6 className='fw-700'>{scopeName}</h6>

                                                        {!hasSelectedItems ? (
                                                            <p className='text-muted mb-2'>No items selected in this scope</p>
                                                        ) : (
                                                            Object.entries(scopeData).map(([categoryName, categoryItems]) => {
                                                                // Filter for categories with selected items
                                                                const selectedCategoryItems = categoryItems.filter(item => item.alreadySelected);

                                                                return selectedCategoryItems.length > 0 && (
                                                                    <div key={categoryName} className='row mx-0 mb-2'>
                                                                        <div className=' col-md-8 px-0 ps-3'>
                                                                            <h6>{categoryName}</h6>
                                                                            <div className='card'>
                                                                                <Table hover className='mb-0 table-border-bottom-0'>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            <th width="35%">Emission Source</th>
                                                                                            <th width="15%" className='text-center'>CO2 factor</th>
                                                                                            <th width="20%" className='text-center'>Unit</th>
                                                                                            <th width="30%">Notes</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {selectedCategoryItems.map(source => (
                                                                                            <tr key={source.sourceId}>
                                                                                                <td>{source.name}</td>
                                                                                                <td className='text-center'>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2' && GHGPossibleUnits.includes(factor.unit)))
                                                                                                        .map(factor => (factor.efValue))
                                                                                                    }
                                                                                                </td>
                                                                                                <td className='text-center'>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2' && GHGPossibleUnits.includes(factor.unit)))
                                                                                                        .map(factor => (factor.unit))
                                                                                                    }
                                                                                                </td>
                                                                                                <td>
                                                                                                    {source.factors
                                                                                                        .filter(factor => (factor.ghg == 'CO2' && GHGPossibleUnits.includes(factor.unit)))
                                                                                                        .map(factor => (factor.notes))
                                                                                                    }
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </Table>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                );

                                            })
                                        ) :
                                            <div className='OprBoundaryBody02_inner'>
                                                {renderEmptyDataLabelEmission()}
                                            </div>
                                        }
                                    </>
                                )}
                            </div>
                        </Collapse>
                    </div> */}
                </div>
            </div>
            {
                !finalized && !noShowFooter &&
                <div className='text-center p-2 pt-3 GHGInventory_cardFooter '>
                    <Button variant='secondary' className='px-4' onClick={() => prevPage('EmiSource')}>{t('Back.label')}</Button>
                    <Button variant='primary' className='px-4 mx-2' onClick={() => setShowConfirmation(true)}
                        disabled={!hasSelSources || orgData.length == 0 || !hasAnswered}>{t('Finalize.label')}</Button>
                </div>
            }
            {
                showConfirmation &&
                <ConfirmationModal
                    type={'GHG_Inventory'}
                    modalShow={true}
                    messageText={t("FinalizeReportModal.label")}
                    callbackModal={confirmCancel}
                    confirmBtn={t("Confirm.label")}
                    CancelBtn={t("Cancel.label")}
                />
            }

            {
                showMessageModal &&
                <MessageModal
                    modalShow={true}
                    messageText={'You have successfully defined the GHG inventory'}
                    callbackModal={callbackMessageModal}
                    id=""
                    title={'GHG Inventory'}
                />
            }
        </>
    );
}
export default withTranslation()(ReviewAndFinalize);